import {} from './brand.js';
/**
 * The dark theme: the site's only theme, and the dashboard's toggled one.
 *
 * The single constraint that shapes it is the measured one — red on navy is 2.94:1, below even the
 * 3:1 graphics threshold — so red appears here only as a surface (`primary`, `destructive`) with
 * white on top, never as a foreground. The inks are white and one derived light grey.
 */
const dark = {
    colors: {
        background: { from: 'apr-navyDeep' },
        foreground: { from: 'apr-white' },
        card: { from: 'apr-navy' },
        'card-foreground': { from: 'apr-white' },
        // Deeper than the page, not lighter: an overlay on this palette reads as recessed depth more
        // convincingly than as a raised sheet, and it keeps white at 18.2:1.
        popover: { from: 'apr-navyAbyss' },
        'popover-foreground': { from: 'apr-white' },
        primary: { from: 'apr-red' },
        'primary-foreground': { from: 'apr-white' },
        secondary: { from: 'apr-navyLift' },
        'secondary-foreground': { from: 'apr-white' },
        muted: { from: 'apr-navy' },
        // The design's secondary-text grey, `#BFBFBF`, expressed as white at 75% lightness so it is
        // traceable to a brand token rather than a fourth neutral nobody owns.
        'muted-foreground': { from: 'apr-white', lightness: 75 },
        accent: { from: 'apr-gold' },
        // Gold is light. Its ink is the deepest navy, not white.
        'accent-foreground': { from: 'apr-navyAbyss' },
        destructive: { from: 'apr-redDeep' },
        'destructive-foreground': { from: 'apr-white' },
        // Surface separation, and the one token exempt from the 3:1 boundary threshold — see
        // `ink-policy.ts`. A card edge at 3:1 against the page reads as a drawn box rather than an edge.
        border: { from: 'apr-navyLift' },
        // A control boundary, which is meaningful rather than decorative, so it does carry 3:1 — and
        // against `secondary` (navyLift), not just against the page. That is what pushes the lightness
        // this high: at 55% it is 2.46:1 on a navyLift panel, which is a form field with an invisible
        // edge on exactly the surface a form panel uses.
        input: { from: 'apr-navy', saturation: 40, lightness: 66 },
        // The focus ring is gold rather than red: red cannot reach 3:1 on navy, and gold is not the
        // primary action colour, so a focused control is never confusable with a pressed one.
        ring: { from: 'apr-gold' },
    },
    elevation: {
        'elevation-flat': 'none',
        'elevation-raised': '0 1px 2px 0 hsl(var(--apr-navyAbyss) / 0.6), inset 0 1px 0 0 hsl(var(--apr-white) / 0.06)',
        'elevation-overlay': '0 8px 24px -4px hsl(var(--apr-navyAbyss) / 0.7), inset 0 1px 0 0 hsl(var(--apr-white) / 0.08)',
        'elevation-modal': '0 24px 64px -12px hsl(var(--apr-navyAbyss) / 0.85), inset 0 1px 0 0 hsl(var(--apr-white) / 0.1)',
    },
};
/** The light theme: the dashboard's default. Navy becomes the ink and white becomes the page. */
const light = {
    colors: {
        background: { from: 'apr-white' },
        foreground: { from: 'apr-navyDeep' },
        card: { from: 'apr-white' },
        'card-foreground': { from: 'apr-navyDeep' },
        popover: { from: 'apr-white' },
        'popover-foreground': { from: 'apr-navyDeep' },
        primary: { from: 'apr-red' },
        'primary-foreground': { from: 'apr-white' },
        secondary: { from: 'apr-navy', saturation: 30, lightness: 94 },
        'secondary-foreground': { from: 'apr-navyDeep' },
        muted: { from: 'apr-navy', saturation: 30, lightness: 94 },
        'muted-foreground': { from: 'apr-navy', saturation: 25, lightness: 35 },
        accent: { from: 'apr-gold' },
        'accent-foreground': { from: 'apr-navyAbyss' },
        destructive: { from: 'apr-redDeep' },
        'destructive-foreground': { from: 'apr-white' },
        border: { from: 'apr-navy', saturation: 20, lightness: 88 },
        input: { from: 'apr-navy', saturation: 20, lightness: 45 },
        ring: { from: 'apr-navy' },
    },
    elevation: {
        'elevation-flat': 'none',
        'elevation-raised': '0 1px 2px 0 hsl(var(--apr-navyDeep) / 0.08)',
        'elevation-overlay': '0 8px 24px -4px hsl(var(--apr-navyDeep) / 0.12)',
        'elevation-modal': '0 24px 64px -12px hsl(var(--apr-navyDeep) / 0.2)',
    },
};
export const themes = { light, dark };
/** The class each theme is applied with. Matches `darkMode` in the Tailwind preset. */
export const themeClasses = {
    light: 'theme-light',
    dark: 'theme-dark',
};
export const themeNames = Object.keys(themes);
//# sourceMappingURL=themes.js.map